import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { i18n } from '../../../i18n/index.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import {
  approveTelegramCandidate,
  confirmTelegramCandidate,
  listTelegramCandidates,
  resolveTelegramCandidate,
  telegramCandidatesKey,
  type CandidatePayload,
} from '../api/telegram-candidates.client.js';

function locale(): 'ru' | 'en' {
  return i18n.locale === 'en' ? 'en' : 'ru';
}

export function useTelegramCandidates(options: { enabled?: boolean } = {}) {
  const scope = useCurrentScope();
  return useQuery({
    queryKey: telegramCandidatesKey(scope?.workspaceId ?? 'anonymous', locale()),
    queryFn: () => listTelegramCandidates(locale()),
    // The candidates API is owner/admin-only: callers without that role gate
    // the query off instead of eating a guaranteed 403 on every render.
    enabled: scope !== null && options.enabled !== false,
  });
}

export function useTelegramCandidateActions() {
  const client = useQueryClient();
  const scope = useCurrentScope();
  const invalidate = async (): Promise<void> => {
    await client.invalidateQueries({
      queryKey: ['telegram-candidates', scope?.workspaceId ?? 'anonymous'],
    });
  };
  const confirm = useMutation({
    mutationFn: (input: { id: string; version: number; payload: CandidatePayload }) =>
      confirmTelegramCandidate(input.id, locale(), input.version, input.payload),
    onSuccess: invalidate,
  });
  const approve = useMutation({
    mutationFn: (input: { id: string; version: number }) =>
      approveTelegramCandidate(input.id, locale(), input.version),
    onSuccess: invalidate,
  });
  const resolve = useMutation({
    mutationFn: (input: { id: string; version: number; decision: 'reject' | 'needs_context' }) =>
      resolveTelegramCandidate(input.id, locale(), input.version, input.decision),
    onSuccess: invalidate,
  });
  return { confirm, approve, resolve };
}
